import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useAuthStore } from "./stores/auth";

import { ProtectedRoute } from "./components/ProtectedRoute";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage";
import { AdminOverviewPage } from "./pages/AdminOverviewPage";
import { AuditPage } from "./pages/AuditPage";
import { BillingPage } from "./pages/BillingPage";
import { ClientsPage } from "./pages/ClientsPage";
import { HomePage } from "./pages/HomePage";
import { IntervenantsPage } from "./pages/IntervenantsPage";
import { InvoiceDetailPage } from "./pages/InvoiceDetailPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { LoginPage } from "./pages/LoginPage";
import { MembersPage } from "./pages/MembersPage";
import { PricingPage } from "./pages/PricingPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ReconciliationPage } from "./pages/ReconciliationPage";
import { ReminderRulesPage } from "./pages/ReminderRulesPage";
import { ReminderTemplatesPage } from "./pages/ReminderTemplatesPage";
import { SelectTenantPage } from "./pages/SelectTenantPage";
import { SignupPage } from "./pages/SignupPage";
import { UploadReviewPage } from "./pages/UploadReviewPage";
import { UploadsPage } from "./pages/UploadsPage";
import { SalesPage } from "./pages/SalesPage";
import { ProductsPage } from "./pages/ProductsPage";
import { SuppliersPage } from "./pages/SuppliersPage";
import { CompanySettingsPage } from "./pages/CompanySettingsPage";
import { LegalPage } from "./pages/LegalPage";

export default function App() {
  const { accessToken, currentTenant, refreshMe } = useAuthStore();

  // Rafraîchit le quota et plan_info à chaque montage de l'app
  // (évite d'afficher un compteur de factures périmé depuis le localStorage).
  useEffect(() => {
    if (accessToken && currentTenant) {
      refreshMe().catch(() => {/* silencieux si token expiré */});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/accept-invitation/:token"
          element={<AcceptInvitationPage />}
        />
        <Route
          path="/select-tenant"
          element={
            <ProtectedRoute requireTenant={false}>
              <SelectTenantPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <ClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intervenants"
          element={
            <ProtectedRoute>
              <IntervenantsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/uploads"
          element={
            <ProtectedRoute>
              <UploadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/uploads/:id/review"
          element={
            <ProtectedRoute>
              <UploadReviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <InvoicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices/:id"
          element={
            <ProtectedRoute>
              <InvoiceDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedRoute>
              <SalesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute>
              <ProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute>
              <SuppliersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reconciliation"
          element={
            <ProtectedRoute>
              <ReconciliationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/billing"
          element={
            <ProtectedRoute>
              <BillingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/members"
          element={
            <ProtectedRoute>
              <MembersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/audit"
          element={
            <ProtectedRoute>
              <AuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/reminder-rules"
          element={
            <ProtectedRoute>
              <ReminderRulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/reminder-templates"
          element={
            <ProtectedRoute>
              <ReminderTemplatesPage />
            </ProtectedRoute>
          }
        />
        {/* Lot 8.6.3 — Dashboard interne Hsasoft (cross-tenant). Garde côté
            backend via allow-list HSASOFT_ADMIN_EMAILS : on laisse n'importe
            quel utilisateur authentifié atteindre la route, l'API renverra
            403/404 et la page affichera l'erreur correspondante. */}
        <Route
          path="/admin/hsasoft"
          element={
            <ProtectedRoute>
              <AdminOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/company"
          element={
            <ProtectedRoute>
              <CompanySettingsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
